import { defineDocument } from '../../schema';
export const blogPoppyGetsPunchyDocument = defineDocument({
    id: 'blog:poppy-gets-punchy',
    kind: 'blog',
    slug: 'poppy-gets-punchy',
    route: '/blog/poppy-gets-punchy',
    seo: {
        title: 'Poppy Gets Punchy',
        description: 'Faster gameplay, two new personalities, and polish across the whole platform',
        imageAsset: 'blogPoppyPunchy',
    },
    hero: {
        eyebrow: [
            {
                type: 'text',
                text: 'Airhead Blog',
            },
        ],
        title: [
            {
                type: 'text',
                text: 'Poppy Gets Punchy',
            },
        ],
        description: [
            {
                type: 'text',
                text: 'Faster gameplay, two new personalities, and polish across the whole platform',
            },
        ],
        imageAsset: 'blogPoppyPunchy',
        imageAlt: 'Poppy Gets Punchy',
        showDescription: true,
        background: 'blurred-image',
    },
    meta: {
        author: 'Jay',
        publishedAt: new Date('2026-02-13T00:00:00.000Z'),
        tags: ['features'],
    },
    sections: [
        {
            id: 'article',
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: "This update is about more game, less ramble. We tightened up Poppy's commentary so games move faster and the energy stays high. We added two new personalities. We polished the platform and every game in the library. The result: smoother, punchier party games.",
                        },
                    ],
                },
            ],
            sections: [
                {
                    id: 'article__less-rambling-more-personality',
                    title: [
                        {
                            type: 'text',
                            text: 'Less Rambling, More Personality',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'With a huge focus on improving brevity of commentary and game context awareness, games flow better than ever. You get to the fun faster, and the pace stays up. That was the goal: keep the energy high and the downtime low.',
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: "The depth of Poppy's personalities has gotten better while at the same time insulating important parts of the experience, such as game instructions and content, from being influenced too heavily by Poppy's style. This clarity allows players to follow along more easily and saves the personality for the moments you actually want it.",
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'Two new personalities were added to the mix, ',
                                },
                                {
                                    type: 'strong',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'Slacker',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ' (bored, deadpan, unbothered) and ',
                                },
                                {
                                    type: 'strong',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'Villain',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ' (menacing, mischievous, scheming), increasing the personality possibilities to 48 unique combinations. Customize your ',
                                },
                                {
                                    type: 'link',
                                    href: '/discover/features/user-preferences#host-style',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'Host Style',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ' in the new interface for picking and previewing ',
                                },
                                {
                                    type: 'link',
                                    href: '/discover/features/poppy',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'your Poppy',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: '.',
                                },
                            ],
                        },
                    ],
                },
                {
                    id: 'article__faster-safer-smarter',
                    title: [
                        {
                            type: 'text',
                            text: 'Faster, Safer, Smarter',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'We upgraded our core infrastructure for faster response times and better security. The platform is more reliable and responsive.',
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'link',
                                    href: '/discover/features/audio',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'Audio',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: " levels were not set at a good level for streamers and remote games. Music was too loud relative to Poppy's voice and the voices of people on stream or on the call. We looked at what hosts were setting their ",
                                },
                                {
                                    type: 'link',
                                    href: '/discover/features/audio#audio-experience__volume-controls',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'volume defaults',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ' to and used that to pick the new default.',
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'Beyond that, we made a ton of bug fixes and enhancements across the platform: image generation, player avatars, sound effects, menus, and error handling.',
                                },
                            ],
                        },
                    ],
                },
                {
                    id: 'article__polish-across-every-game',
                    title: [
                        {
                            type: 'text',
                            text: 'Polish Across Every Game',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'link',
                                    href: '/discover/games/finish-each-others-sandwiches',
                                    children: [
                                        {
                                            type: 'text',
                                            text: "Finish Each Other's Sandwiches",
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ' has easier scoring and more straightforward answer options. ',
                                },
                                {
                                    type: 'link',
                                    href: '/discover/games/ego-maniacs',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'Ego Maniacs',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ' has more reliable trophy generation and a cleaner flow. ',
                                },
                                {
                                    type: 'link',
                                    href: '/discover/games/meme-mash',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'Meme Mash',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ' runs in four turns instead of five so it hits the sweet spot. In ',
                                },
                                {
                                    type: 'link',
                                    href: '/discover/games/quinspire',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'Quinspire',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ' the theme is no longer overlapping the top word of the word stack.',
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'link',
                                    href: '/discover/games/second-opinions',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'Second Opinions',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ' lets bad doctors win when they eliminate the good ones and handles edge cases better. ',
                                },
                                {
                                    type: 'link',
                                    href: '/discover/games/wtf-is-happening',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'WTF Is Happening',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ' no longer repeats newspaper sections in the same game and handles acronym answers more flexibly. Similar story for ',
                                },
                                {
                                    type: 'link',
                                    href: '/discover/games/too-many-cooks',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'Too Many Cooks',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ', which now avoids asking questions that are too similar in the Chef Survey.',
                                },
                            ],
                        },
                    ],
                },
                {
                    id: 'article__let-s-go',
                    title: [
                        {
                            type: 'text',
                            text: "Let's Go",
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'Quicker, better party games are here. ',
                                },
                                {
                                    type: 'link',
                                    href: '/library',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'Start one now',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: '. Questions or feedback on the updates? Come chat with the community in ',
                                },
                                {
                                    type: 'link',
                                    href: 'https://discord.gg/rfFTVAqHwH',
                                    children: [
                                        {
                                            type: 'text',
                                            text: "Poppy's Pals Discord",
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: '.',
                                },
                            ],
                        },
                    ],
                },
            ],
        },
        {
            id: 'article-actions',
            widgets: [
                {
                    type: 'relatedDocuments',
                    limit: 4,
                },
            ],
        },
    ],
});
//# sourceMappingURL=poppy-gets-punchy.js.map